import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import { useDispatch } from "react-redux";
import LoadingOverlay from "../helper-components/overlay-loading";
import {
  pastBillsFamilyMembersAction,
  salesItemsBySotIdAction,
  topBillsAction
} from "../../redux/actions/invoices-action";
import TabsComponent from "../helper-components/tabs-component";
import { truncateString } from "../helper-components/helper-function";
import SelectIcon from "../../assets/icons/select-icon";
import errorToast from "../helper-components/error-toast";
import moment from "moment";
import { url } from "../../redux/config";
import { api } from "../../redux/apis/api";
import { useHotkeys } from "../helper-components/use-hotkeys";
import { extractPhoneNumber } from "../../utils/phone";

const SimplePager = ({ page, hasNextPage, onChange }) => (
  <div className="flex items-center justify-end gap-3 px-3 h-[36px] shrink-0 border-t border-[var(--create-table-border)]">
    <button
      type="button"
      disabled={page <= 1}
      onClick={() => onChange(page - 1)}
      className="px-3 py-1 text-[12px] font-semibold rounded-md border border-[var(--create-table-border)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--create-table-row-hover-bg)]"
    >
      Prev
    </button>
    <span className="text-[12px] text-[var(--create-table-row-fg-color)]">
      Page {page}
    </span>
    <button
      type="button"
      disabled={!hasNextPage}
      onClick={() => onChange(page + 1)}
      className="px-3 py-1 text-[12px] font-semibold rounded-md border border-[var(--create-table-border)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--create-table-row-hover-bg)]"
    >
      Next
    </button>
  </div>
);

export default function BillingPastBillPanel({ open, setOpen }) {
  const addref = useRef();

  useHotkeys("a", () => {
    if (addref.current) {
      addref.current.click();
    }
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const { values, setFieldValue } = useFormikContext();
  const [selectedRow, setSelectedRow] = useState("");
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const phone = extractPhoneNumber(values?.phone?.label);
  const [pastorders, setPastOrders] = useState({});
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState("Past Bills");
  const itemTabs = ["Past Bills", "Top Products"];
  const dayFilterOptions = [30, 60, 90];
  const [noOfDays, setNoOfDays] = useState(30);
  const [pastBillsPage, setPastBillsPage] = useState(1);
  const [pastBillsHasNext, setPastBillsHasNext] = useState(false);
  const [topProductsPage, setTopProductsPage] = useState(1);
  const [topProductsHasNext, setTopProductsHasNext] = useState(false);
  const [billItemsPage, setBillItemsPage] = useState(1);
  const [billItemsHasNext, setBillItemsHasNext] = useState(false);
  const activePage =
    currentTab === "Past Bills" ? pastBillsPage : topProductsPage;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    if (currentTab === "Past Bills") {
      dispatch(
        pastBillsFamilyMembersAction(
          {
            phone_number: phone,
            no_of_days: noOfDays,
            pageNumber: pastBillsPage,
          },
          onSuccessPastBills,
          onError
        )
      );
    } else {
      dispatch(
        topBillsAction(
          {
            cmr_phone_number: phone,
            days: noOfDays,
            pageNumber: topProductsPage,
          },
          onSuccessTopProducts,
          onError
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noOfDays, currentTab, activePage]);

  const onSuccessTopProducts = (data) => {
    setError(false);
    setLoading(false);
    const responseData = data?.data ?? {};
    const topProducts = Array.isArray(responseData)
      ? responseData
      : responseData?.topBills ?? responseData?.topProducts ?? [];
    setPastOrders((prev) => ({ ...prev, topProducts }));
    setTopProductsHasNext(Boolean(responseData?.pagination?.hasNextPage));
  };

  const onSuccessPastBills = (data) => {
    setError(false);
    setLoading(false);
    const responseData = data?.data ?? {};
    setPastOrders((prev) => ({
      ...prev,
      allItemsData: responseData?.pastBills ?? [],
    }));
    setDataCount(responseData?.pagination?.total ?? 0);
    setPastBillsHasNext(Boolean(responseData?.pagination?.hasNextPage));
  };
  const onError = () => {
    setError(true);
    setLoading(false);
  };

  const handleDayFilterChange = (days) => {
    setNoOfDays(days);
    setPastBillsPage(1);
    setTopProductsPage(1);
    setSelectedRow("");
    setPastOrders((prev) => ({ ...prev, itemsData: [] }));
  };

  const handlePastBillsPageChange = (page) => {
    if (page < 1) return;
    setSelectedRow("");
    setPastOrders((prev) => ({ ...prev, itemsData: [] }));
    setPastBillsPage(page);
  };

  const handleTopProductsPageChange = (page) => {
    if (page < 1) return;
    setTopProductsPage(page);
  };

  function selectBillFun(id, page = 1) {
    setLoading(true);
    setSelectedRow(id);
    setBillItemsPage(page);
    if (id) {
      dispatch(
        salesItemsBySotIdAction(
          { sot_id: id, pageNumber: page },
          onSuccessView,
          onErrorView
        )
      );
    } else {
      setPastOrders({
        ...pastorders,
        itemsData: [],
      });
    }
  }
  const onSuccessView = (data) => {
    setLoading(false);

    const uniqueItems = [];
    const itemIds = new Set();

    (data?.data?.salesItems || []).forEach((item) => {
      const sellableQty = parseFloat(item?.total_sellable_quantity) || 0;

      if (!itemIds.has(item.item_id) && sellableQty > 0) {
        itemIds.add(item.item_id);
        uniqueItems.push(item);
      }
    });

    setPastOrders({
      ...pastorders,
      itemsData: uniqueItems,
    });
    setBillItemsHasNext(Boolean(data?.data?.pagination?.hasNextPage));
  };

  const onErrorView = (data) => {
    setLoading(false);
    setPastOrders({ ...pastorders, itemsData: [] });
    setBillItemsHasNext(false);
    errorToast(data?.data?.message);
  };

  const handleBillItemsPageChange = (page) => {
    if (page < 1 || !selectedRow) return;
    selectBillFun(selectedRow, page);
  };
  const handleCheckboxChange = (e, type = "single") => {
    const keyToUse = currentTab === "Past Bills" ? "itemsData" : "topProducts";
    const itemsList = pastorders?.[keyToUse];

    if (type === "all") {
      const allIds = itemsList?.map((item) => item?.item_id);
      const isAllSelected = allIds?.every((id) => selectedItems.includes(id));
      setSelectedItems(isAllSelected ? [] : allIds);
    } else {
      const itemId = e.target.id;
      if (e.target.checked) {
        setSelectedItems((prev) => [...prev, itemId]);
      } else {
        setSelectedItems((prev) => prev.filter((id) => id !== itemId));
      }
    }
  };

  const isItemChecked = (rowData) => selectedItems.includes(rowData?.item_id);
  const handleQuantityChange = (e, itemId) => {
    const newQty = e.target.value;

    if (newQty === "" || !isNaN(newQty)) {
      const keyToUpdate =
        currentTab === "Past Bills" ? "itemsData" : "topProducts";

      const updatedItems = pastorders[keyToUpdate].map((item) => {
        if (item.item_id === itemId) {
          // Past bills carry the stock on hand as total_sellable_quantity;
          // the top-bills payload names the same figure
          // item_sellable_quantity.
          const maxQty =
            parseFloat(
              item.total_sellable_quantity ?? item.item_sellable_quantity
            ) || 0;
          let validatedQty = newQty;
          if (newQty !== "" && parseFloat(newQty) > maxQty) {
            validatedQty = maxQty.toString();
          }

          return {
            ...item,
            quantity: validatedQty,
          };
        }
        return item;
      });

      setPastOrders({
        ...pastorders,
        [keyToUpdate]: updatedItems,
      });
    }
  };

  // Past-bill and top-product rows are sales records: they carry no product
  // master data, so the billing grid's Item Code / Pack Size / UOM / SCH / HSN
  // columns rendered as "--" for every item added from this panel. Resolve them
  // from the product master the same way the promotional-item path does, and
  // keep whatever the row already carries in preference to the lookup.
  const fetchItemMasterDetails = async (item) => {
    try {
      const masterResponse = await api.get(
        `/product/get?field=item_id&value=${item?.item_id}`
      );
      const master = masterResponse?.data?.data?.[0] ?? {};
      const itemUomId = master?.item_uom_id || master?.item_uom || "";
      let itemUom = item?.item_uom || "";

      // /product/get returns the UOM reference; the display name comes from the
      // same endpoint the auto-batch billing flow uses for the UOM column.
      if (!itemUom && itemUomId) {
        try {
          const uomResponse = await api.get(
            `/product/getItemAvailableStockAndUOM?item_id=${item?.item_id}&item_uom=${itemUomId}`
          );
          itemUom = uomResponse?.data?.data?.item_uom || "";
        } catch (uomError) {
          // fall back to the master value below
        }
      }

      const details = {
        item_code: item?.item_code || master?.item_code,
        item_pack_size: item?.item_pack_size ?? master?.item_pack_size,
        // master.item_uom is the display name only when the id lives in
        // item_uom_id; otherwise it is the id itself and must not be shown.
        item_uom: itemUom || (master?.item_uom_id ? master?.item_uom : ""),
        item_uom_id: item?.item_uom_id || itemUomId,
        item_schedule: item?.item_schedule || master?.item_schedule,
        item_hsn: item?.item_hsn || master?.item_hsn,
        item_generic_name: item?.item_generic_name || master?.item_generic_name,
        molecule_name: item?.molecule_name || master?.molecule_name,
        item_manufacturer_name:
          item?.item_manufacturer_name || master?.item_manufacturer_name,
        item_manufacturer_id:
          item?.item_manufacturer_id || master?.item_manufacturer,
      };

      // Drop the blanks so a field the master does not carry keeps rendering as
      // "--" instead of an empty cell.
      return Object.fromEntries(
        Object.entries(details).filter(
          ([, value]) => value !== undefined && value !== null && value !== ""
        )
      );
    } catch (error) {
      // The item still gets added — only the master columns stay blank.
      return {};
    }
  };

  // COMMITED QTY RESERVATION
  // Items added from this panel reserve stock exactly like the ones typed into
  // the billing grid (see billing-items-section), so another terminal can't sell
  // the same units while this bill is open. commited_qty is always a delta in
  // base (unit) qty: what the item holds on a batch now minus what it held
  // before (0 when it wasn't on the bill yet).
  const commitedQtyUrl = `${url}/add-or-update/commited-qty`;

  // A row's quantity in base (unit) qty. Rows this panel adds are already in
  // base units; rows the billing grid added in a non-piece billing UOM carry
  // billing_base_quantity and need it applied.
  const getRowUnitQty = (row) => {
    const qty = parseFloat(row?.quantity || 0) || 0;
    const baseQty = parseFloat(row?.billing_base_quantity || 0) || 0;
    return baseQty > 0 ? qty * baseQty : qty;
  };

  // Throws on failure so the caller can abort the add.
  const updateCommitedQty = async ({
    item_id,
    item_batch_number,
    item_rack_location,
    commited_qty,
    is_delete_item = false,
  }) => {
    await api.post(commitedQtyUrl, {
      item_id,
      item_batch_number,
      item_rack_location,
      commited_qty,
      is_delete_item,
    });
  };

  async function addtoBillFn(filteredItems) {
    const updatedData = [...(values?.data ?? [])]; // 👈 shared array
    // The reservation calls run before the rows land on the bill, so the panel
    // stays blocked until every selected item is reserved.
    setLoading(true);

    const handleQuantityChange = async (
      qty,
      item,
      item_refill_days,
      item_shortage_qty,
      values,
      sharedUpdatedData
    ) => {
      const baseIndex = sharedUpdatedData.length;

      // remove duplicate item
      const cleanedData = sharedUpdatedData.filter(
        (existingItem) => existingItem.item_id !== item.item_id
      );

      const stockApiUrl = `${url}/v1/inventory/stock/items?item_id=${item.item_id}&item_quantity=${qty}&cmr_phone_number=${extractPhoneNumber(values?.phone?.label)}&item_discount_percentage=${values?.tempRowData?.item_discount_percentage}`;

      if (parseFloat(qty) > 0) {
        try {
          const [stockApiResponse, itemMasterDetails] = await Promise.all([
            api.get(stockApiUrl),
            fetchItemMasterDetails(item),
          ]);
          const batchDetail = stockApiResponse?.data?.data?.itemDetails;

          if (stockApiResponse?.data?.code === 200 && batchDetail?.length > 0) {
            // Reserve the newly allocated stock before the rows land on the bill —
            // a rejection here throws to the catch below, leaving the bill
            // untouched. The stock API re-allocates the whole item (and the
            // dedupe above drops every row it previously held), so every batch it
            // no longer holds is released and every batch it now holds is
            // reserved. Keyed by batch + rack location because that is what the
            // API reserves against and what identifies a row here.
            const batchKey = (batchNumber, rackLocation) =>
              `${batchNumber ?? ""}||${rackLocation ?? ""}`;
            const accumulate = (map, batchNumber, rackLocation, unitQty) => {
              const key = batchKey(batchNumber, rackLocation);
              const entry = map.get(key) ?? {
                item_batch_number: batchNumber,
                item_rack_location: rackLocation,
                unitQty: 0,
              };
              entry.unitQty += unitQty;
              map.set(key, entry);
            };

            // Promotional rows are added by the offer machinery without a
            // reservation, so they neither release nor offset the delta.
            const previousByBatch = new Map();
            for (const previousRow of sharedUpdatedData) {
              if (
                previousRow?.item_id !== item?.item_id ||
                previousRow?.is_promotional
              )
                continue;
              accumulate(
                previousByBatch,
                previousRow?.item_batch_number,
                previousRow?.item_rack_location,
                getRowUnitQty(previousRow)
              );
            }
            const currentByBatch = new Map();
            for (const allocated of batchDetail) {
              accumulate(
                currentByBatch,
                allocated?.batchNumber,
                allocated?.item_rack_location,
                parseFloat(allocated?.quantity) || 0
              );
            }

            for (const [key, previous] of previousByBatch) {
              if (currentByBatch.has(key) || previous.unitQty === 0) continue;
              await updateCommitedQty({
                item_id: item?.item_id,
                item_batch_number: previous.item_batch_number,
                item_rack_location: previous.item_rack_location,
                commited_qty: -previous.unitQty,
              });
            }
            for (const [key, current] of currentByBatch) {
              const previousUnitQty = previousByBatch.get(key)?.unitQty ?? 0;
              if (current.unitQty === previousUnitQty) continue;
              await updateCommitedQty({
                item_id: item?.item_id,
                item_batch_number: current.item_batch_number,
                item_rack_location: current.item_rack_location,
                commited_qty: current.unitQty - previousUnitQty,
              });
            }

            for (
              let itemDetailIndex = 0;
              itemDetailIndex < batchDetail.length;
              itemDetailIndex++
            ) {
              const itemDetail = batchDetail[itemDetailIndex];
              const itemBatchList = stockApiResponse.data.data?.itemBatchList;
              const updatedRowIndex = baseIndex + itemDetailIndex;

              const total_amount = (
                parseFloat(itemDetail.item_unit_price) *
                  parseFloat(itemDetail.quantity) -
                parseFloat(itemDetail.item_discount_amount)
              ).toFixed(2);

              const item_final_price = (
                total_amount / parseFloat(itemDetail.quantity) || 0
              ).toFixed(2);

              const item_margin_percentage =
                (
                  ((parseFloat(item_final_price) -
                    parseFloat(itemDetail.item_batch_final_purchase_rate)) /
                    parseFloat(itemDetail.item_batch_final_purchase_rate)) *
                  100
                ).toFixed(2) || 0;

              const updatedDataRow = {
                ...item,
                ...itemMasterDetails,
                item_discount_amount: itemDetail.item_discount_amount,
                item_tax_amount: itemDetail.item_tax_amount.toFixed(2),
                item_discount_percentage:
                  itemDetail.item_discount_percentage || 0,
                item_unit_price: itemDetail.item_unit_price,
                item_price_wiithout_tax: (
                  parseFloat(itemDetail.item_unit_price) *
                    parseFloat(itemDetail.quantity) -
                    parseFloat(itemDetail.item_discount_amount) -
                    parseFloat(itemDetail.item_tax_amount) || 0
                ).toFixed(2),
                item_batch_number: itemDetail.batchNumber,
                item_rack_location: itemDetail.item_rack_location,
                item_batch_final_purchase_rate:
                  itemDetail.item_batch_final_purchase_rate || 0,
                item_exp_date: moment(itemDetail.expiryDate).format(
                  "YYYY-MM-DD"
                ),
                to_bin_id: itemDetail.to_bin_id,
                quantity: itemDetail.quantity,
                instock: true,
                item_gst: itemDetail.item_total_tax_percentage,
                item_sgst: (parseFloat(itemDetail.item_tax_amount) / 2).toFixed(
                  2
                ),
                item_cgst: (parseFloat(itemDetail.item_tax_amount) / 2).toFixed(
                  2
                ),
                item_igst: 0,
                item_final_price,
                item_margin_percentage,
                item_total_amount: total_amount,
                itemBatchList: itemBatchList ?? [],
                item_total_tax_percentage: itemDetail.item_total_tax_percentage,
                minDiscountPercentage:
                  parseFloat(itemDetail.minDiscountPercentage) || 0,
                maxDiscountPercentage:
                  parseFloat(itemDetail.maxDiscountPercentage) || 0,
                item_refill_days,
                // the shortage is for the item, not per batch: kept on its first line
                item_shortage_qty: itemDetailIndex === 0 ? item_shortage_qty : 0,
                is_predefined_dis:
                  parseFloat(itemDetail.item_discount_percentage) > 0,
                // The panel bills in loose units only. These two drive the
                // grid's Pack/Loose Qty columns and the footer's cost split
                // (pack_qty * pack_size + loose_qty), so loose_qty has to be
                // the quantity billed on this batch row — it used to be the
                // item's whole available stock, which showed the wrong Loose
                // Qty and inflated the invoice cost/profit calculation.
                item_pack_qty: 0,
                item_loose_qty: itemDetail.quantity,
              };

              cleanedData.splice(updatedRowIndex, 0, updatedDataRow);
            }

            // Now update sharedUpdatedData reference
            sharedUpdatedData.length = 0; // Clear existing
            sharedUpdatedData.push(...cleanedData);
          } else {
            errorToast(
              `Product ${item.item_name} with quantity ${qty} is not available`
            );
          }
        } catch (error) {
          if (
            error?.response?.data?.data?.itemDetails?.item_sellable_quantity
          ) {
            errorToast(
              `${item.item_name} available quantity is ${error.response.data.data.itemDetails.item_sellable_quantity}`
            );
          } else {
            errorToast(
              error?.response?.data?.message ?? "Something went wrong"
            );
          }
        }
      } else {
        errorToast("Quantity should be greater than 0");
      }
    };

    for (let index = 0; index < filteredItems?.length; index++) {
      const itemData = filteredItems[index];
      const { quantity, item_refill_days, item_shortage_qty } = itemData;

      await handleQuantityChange(
        quantity,
        itemData,
        item_refill_days,
        item_shortage_qty,
        values,
        updatedData // 👈 pass shared reference
      );
    }

    setLoading(false);

    const sot_total_gst = updatedData.reduce(
      (total, item) => total + parseFloat(item?.item_tax_amount || 0),
      0
    );
    const sot_total_discount = updatedData.reduce(
      (total, item) => total + parseFloat(item?.item_discount_amount || 0),
      0
    );
    const sot_sub_total = updatedData.reduce(
      (subtotal, item) =>
        subtotal +
        parseFloat(item?.item_unit_price || 0) *
          parseFloat(item?.quantity || 0),
      0
    );
    const sot_total_amount = updatedData.reduce(
      (total, item) => total + parseFloat(item?.item_total_amount || 0),
      0
    );
    const rounded_total = Math.round(sot_total_amount);
    const roundoff = (rounded_total - sot_total_amount)?.toFixed(2);

    setFieldValue("data", updatedData);
    setFieldValue("sot_total_gst", sot_total_gst.toFixed(2));
    setFieldValue("sot_total_discount", sot_total_discount.toFixed(2));
    setFieldValue("sot_sub_total", sot_sub_total.toFixed(2));
    setFieldValue("sot_total_amount", rounded_total.toFixed(2));
    setFieldValue("sot_round_off_value", roundoff);
    setFieldValue("sot_invoice_discount", 0);
    setFieldValue("sot_invoice_discount_percentage", 0);
    setFieldValue("sot_is_invoice_dis_percentage", false);
    setFieldValue("header_inv_dis_per", "");
    setOpen(false);
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setOpen(false);
          setSelectedRow("");
          setSelectedItems([]);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full w-[400px] sm:w-[1000px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}
                <div
                  className="h-[61px] bg-[var(--slide-panel-head-bg)] px-3 flex items-center justify-between border-b"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <div
                    style={{
                      maxHeight: "32px",
                      color: "var(--slide-panel-h-fg)",
                      fontSize: "var(--slide-panel-h-fs)",
                      fontWeight: "var(--slide-panel-h-fw)",
                    }}
                    className="min-w-[75%] "
                  >
                    Past Bills & Top Products
                  </div>
                  <AddButton
                    onclkFn={() => {
                      setOpen(false);
                      // setProductDetails({ item_id: "" });
                    }}
                    icon={
                      <CloseIcon
                        width="12px"
                        height="12px"
                        fill="var(--close-button-fg)"
                      />
                    }
                    text={
                      <span>
                        Close <span className="opacity-50">(X)</span>{" "}
                      </span>
                    }
                    className="flex h-[32px]  items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                  />
                </div>

                {/* Product Detail Panel */}
                <div
                  style={{
                    maxHeight: `calc(100% - 115px)`,
                    minHeight: `calc(100% - 115px)`,
                    height: `calc(100% - 115px)`,
                  }}
                >
                  <div className="px-3 flex items-center justify-between gap-3">
                    <TabsComponent
                      handleClickfn={(item) => {
                        setCurrentTab(item);
                        setSelectedItems([]);
                      }}
                      itemTabs={itemTabs}
                      setCurrentTab={setCurrentTab}
                      currentTab={currentTab}
                    />
                    <div className="flex items-center gap-1 bg-[var(--nav-head-bg)] rounded-lg p-1">
                      {dayFilterOptions.map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => handleDayFilterChange(days)}
                          className={`px-3 py-1.5 text-[12.5px] font-semibold rounded-md whitespace-nowrap transition ${
                            noOfDays === days
                              ? "bg-[var(--tab-comp-sel-fg)] text-white"
                              : "text-[var(--tab-comp-fg)] hover:opacity-70"
                          }`}
                        >
                          Last {days} days
                        </button>
                      ))}
                    </div>
                  </div>
                  {currentTab === "Past Bills" ? (
                    <div
                      style={{
                        maxHeight: `calc(100% - 4rem)`,
                        minHeight: `calc(100% - 4rem)`,
                        height: `calc(100% - 4rem)`,
                      }}
                      className="flex flex-col gap-5 px-3 "
                    >
                      <div className="tab-container h-[48%] border border-[var(--create-table-border)] rounded-[8px] flex flex-col">
                       <div className="flex-1 min-h-0 overflow-y-auto">
                        <table className="w-full border-collapse mt-0   ">
                          <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                            <tr className="relative h-[50px]">
                              {pastBillsHeading
                                ?.filter(
                                  (heading) => heading?.enabled !== false
                                )
                                ?.map((heading, index) => (
                                  <th
                                    style={{
                                      minWidth: `${heading?.minWidth}px`,
                                    }}
                                    key={index}
                                    className={`px-3 whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]`}
                                  >
                                    <div
                                      className="flex items-center gap-2 w-full"
                                      style={{
                                        justifyContent:
                                          heading?.alignment === "right"
                                            ? "flex-end"
                                            : heading?.alignment === "center"
                                            ? "center"
                                            : "flex-start",
                                      }}
                                    >
                                      {heading?.label}
                                    </div>
                                  </th>
                                ))}
                            </tr>
                          </thead>
                          <tbody className="h-full">
                            {pastorders?.allItemsData?.map(
                              (rowData, rowIndex) => (
                                <tr
                                  onClick={() => {
                                    selectBillFun(
                                      rowData?.sot_id === selectedRow
                                        ? ""
                                        : rowData?.sot_id
                                    );
                                  }}
                                  className={` h-[50px] ${
                                    selectedRow === rowData?.sot_id
                                      ? "bg-[var(--create-table-sel-row-bg)]"
                                      : "hover:bg-[var(--create-table-row-hover-bg)] bg-[var(--create-table-row-bg)]"
                                  }    `}
                                  key={rowIndex}
                                >
                                  {pastBillsHeading
                                    ?.filter(
                                      (heading) => heading?.enabled !== false
                                    )
                                    ?.map((heading, index) => {
                                      return (
                                        <td
                                          style={{
                                            minWidth: `${heading?.minWidth}px`,
                                            textAlign: heading?.alignment, // Keep textAlign for normal text cells
                                          }}
                                          key={index}
                                          className="border-b border-[var(--create-table-border)] px-3 text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap cursor-pointer"
                                        >
                                          {heading?.value === "actions" ? (
                                            rowData?.sot_id === selectedRow ? (
                                              <SelectIcon />
                                            ) : (
                                              ""
                                            )
                                          ) : (
                                            truncateString(
                                              rowData[heading.value] || "--",
                                              70
                                            )
                                          )}
                                        </td>
                                      );
                                    })}
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                       </div>
                       <SimplePager
                         page={pastBillsPage}
                         hasNextPage={pastBillsHasNext}
                         onChange={handlePastBillsPageChange}
                       />
                      </div>

                      <div
                        className="tab-container h-[48%] border border-[var(--create-table-border)] rounded-[8px] flex flex-col"
                        style={{ overflowX: "hidden" }}
                      >
                       <div className="flex-1 min-h-0 overflow-y-auto">
                        <table className="w-full table-fixed border-collapse mt-0   ">
                          <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                            <tr className="relative h-[50px]">
                              {pastProductsHeading
                                ?.filter(
                                  (heading) => heading?.enabled !== false
                                )
                                ?.map((heading, index) => (
                                  <th
                                    style={{
                                      width: heading?.width,
                                    }}
                                    key={index}
                                    className={`px-3 whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)] text-left`}
                                  >
                                    {heading?.value === "action" ? (
                                      <input
                                        type="checkbox"
                                        checked={
                                          pastorders?.itemsData?.length > 0 &&
                                          pastorders?.itemsData?.every((item) =>
                                            selectedItems.includes(
                                              item?.item_id
                                            )
                                          )
                                        }
                                        onChange={(e) =>
                                          handleCheckboxChange(e, "all")
                                        }
                                        className="custom-checkbox form-check-input"
                                      />
                                    ) : (
                                      <div
                                        className="flex items-center gap-2 w-full"
                                        style={{
                                          justifyContent:
                                            heading?.alignment === "right"
                                              ? "flex-end"
                                              : heading?.alignment === "center"
                                              ? "center"
                                              : "flex-start",
                                        }}
                                      >
                                        {heading?.label}
                                      </div>
                                    )}
                                  </th>
                                ))}
                            </tr>
                          </thead>
                          <tbody className="h-full">
                            {pastorders?.itemsData?.length > 0 ? (
                              <>
                                {pastorders?.itemsData?.map(
                                  (rowData, rowIndex) => (
                                    <tr
                                      className={`hover:bg-[var(--create-table-row-hover-bg)] bg-[var(--create-table-row-bg)]  h-[50px] px-3 `}
                                      key={rowIndex}
                                    >
                                      {pastProductsHeading
                                        ?.filter(
                                          (heading) =>
                                            heading?.enabled !== false
                                        )
                                        ?.map((heading, index) => {
                                          return (
                                            <td
                                              style={{
                                                width: heading?.width,
                                                textAlign: heading?.alignment, // Keep textAlign for normal text cells
                                              }}
                                              key={index}
                                              className="border-b border-[var(--create-table-border)] px-3 text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer"
                                            >
                                              {heading?.value === "action" ? (
                                                <input
                                                  type="checkbox"
                                                  checked={isItemChecked(
                                                    rowData
                                                  )}
                                                  id={rowData?.item_id}
                                                  onChange={(e) =>
                                                    handleCheckboxChange(e)
                                                  }
                                                  className="custom-checkbox form-check-input"
                                                />
                                              ) : heading?.value ===
                                                "quantity" ? (
                                                <input
                                                  disabled={
                                                    !isItemChecked(rowData)
                                                  }
                                                  type="number"
                                                  value={rowData.quantity || ""}
                                                  onChange={(e) =>
                                                    handleQuantityChange(
                                                      e,
                                                      rowData.item_id
                                                    )
                                                  }
                                                  className="min-h-[33px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons w-[4rem]"
                                                />
                                              ) : (
                                                truncateString(
                                                  rowData[heading.value] ||
                                                    "--",
                                                  70
                                                )
                                              )}
                                            </td>
                                          );
                                        })}
                                    </tr>
                                  )
                                )}
                              </>
                            ) : (
                              <tr>
                                <td
                                  colSpan={
                                    pastProductsHeading?.filter(
                                      (h) => h?.enabled !== false
                                    ).length
                                  }
                                  className="text-center py-4 text-[length:var(--tab-empty-comp-fs)] border-b border-[var(--create-table-border)] font-[var(--tab-empty-comp-fw)] text-[var(--tab-empty-comp-fg)]"
                                >
                                  Pick a bill to see items
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                       </div>
                       <SimplePager
                         page={billItemsPage}
                         hasNextPage={billItemsHasNext}
                         onChange={handleBillItemsPageChange}
                       />
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        maxHeight: `calc(100% - 4rem)`,
                        minHeight: `calc(100% - 4rem)`,
                        height: `calc(100% - 4rem)`,
                      }}
                      className="flex flex-col gap-5 px-3 "
                    >
                      <div
                        className="tab-container h-[48%] border border-[var(--create-table-border)] rounded-[8px] flex flex-col"
                        style={{ overflowX: "hidden" }}
                      >
                       <div className="flex-1 min-h-0 overflow-y-auto">
                        <table className="w-full table-fixed border-collapse mt-0   ">
                          <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                            <tr className="relative h-[50px]">
                              {topProductsHeading
                                ?.filter(
                                  (heading) => heading?.enabled !== false
                                )
                                ?.map((heading, index) => (
                                  <th
                                    style={{
                                      width: heading?.width,
                                    }}
                                    key={index}
                                    className={`px-3 whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)] text-left`}
                                  >
                                    {heading?.value === "action" ? (
                                      <input
                                        type="checkbox"
                                        checked={
                                          pastorders?.topProducts?.length > 0 &&
                                          pastorders?.topProducts?.every(
                                            (item) =>
                                              selectedItems.includes(
                                                item?.item_id
                                              )
                                          )
                                        }
                                        onChange={(e) =>
                                          handleCheckboxChange(e, "all")
                                        }
                                        className="custom-checkbox form-check-input"
                                      />
                                    ) : (
                                      <div
                                        className="flex items-center gap-2 w-full"
                                        style={{
                                          justifyContent:
                                            heading?.alignment === "right"
                                              ? "flex-end"
                                              : heading?.alignment === "center"
                                              ? "center"
                                              : "flex-start",
                                        }}
                                      >
                                        {heading?.label}
                                      </div>
                                    )}
                                  </th>
                                ))}
                            </tr>
                          </thead>
                          <tbody className="h-full">
                            {pastorders?.topProducts?.length > 0 ? (
                              <>
                                {pastorders?.topProducts?.map(
                                  (rowData, rowIndex) => (
                                    <tr
                                      className={`hover:bg-[var(--create-table-row-hover-bg)] bg-[var(--create-table-row-bg)]  h-[50px] px-3 `}
                                      key={rowIndex}
                                    >
                                      {topProductsHeading
                                        ?.filter(
                                          (heading) =>
                                            heading?.enabled !== false
                                        )
                                        ?.map((heading, index) => {
                                          return (
                                            <td
                                              style={{
                                                width: heading?.width,
                                                textAlign: heading?.alignment, // Keep textAlign for normal text cells
                                              }}
                                              key={index}
                                              className="border-b border-[var(--create-table-border)] px-3 text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer"
                                            >
                                              {heading?.value === "action" ? (
                                                <input
                                                  type="checkbox"
                                                  checked={isItemChecked(
                                                    rowData
                                                  )}
                                                  id={rowData?.item_id}
                                                  onChange={(e) =>
                                                    handleCheckboxChange(e)
                                                  }
                                                  className="custom-checkbox form-check-input"
                                                />
                                              ) : heading?.value ===
                                                "quantity" ? (
                                                <input
                                                  disabled={
                                                    !isItemChecked(rowData)
                                                  }
                                                  type="number"
                                                  value={rowData.quantity || ""}
                                                  onChange={(e) =>
                                                    handleQuantityChange(
                                                      e,
                                                      rowData.item_id
                                                    )
                                                  }
                                                  className="min-h-[33px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons w-[4rem]"
                                                />
                                              ) : (
                                                truncateString(
                                                  rowData[heading.value] ||
                                                    "--",
                                                  70
                                                )
                                              )}
                                            </td>
                                          );
                                        })}
                                    </tr>
                                  )
                                )}
                              </>
                            ) : (
                              <tr>
                                <td
                                  colSpan={
                                    topProductsHeading?.filter(
                                      (h) => h?.enabled !== false
                                    ).length
                                  }
                                  className="text-center py-4 text-[length:var(--tab-empty-comp-fs)] border-b border-[var(--create-table-border)] font-[var(--tab-empty-comp-fw)] text-[var(--tab-empty-comp-fg)]"
                                >
                                  No Products Available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                       </div>
                       <SimplePager
                         page={topProductsPage}
                         hasNextPage={topProductsHasNext}
                         onChange={handleTopProductsPageChange}
                       />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between h-[50px] items-center px-3">
                  <span></span>
                  <AddButton
                    ref={addref}
                    prefixIcon={false}
                    text={
                      <span>
                        Add to Bill <span className="opacity-50">(A)</span>{" "}
                      </span>
                    }
                    className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]                     
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-font-size)] 
    font-[var(--primary-button-font-weight)]"
                    type="button"
                    onclkFn={() => {
                      const sourceData =
                        currentTab === "Past Bills"
                          ? pastorders?.itemsData
                          : pastorders?.topProducts;

                      const filteredItems = sourceData?.filter(
                        (item) => parseFloat(item.quantity) > 0
                      );

                      addtoBillFn(filteredItems);
                    }}
                  />
                </div>

                <LoadingOverlay loading={loading} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

const pastBillsHeading = [
  {
    label: "Store Code",
    value: "store_code",
  },
  {
    label: "Invoice #",
    value: "sot_invoice_number",
  },
  {
    label: "Customer Name",
    value: "cmr_first_name",
  },
  {
    label: "Mobile",
    value: "cmr_phone_number",
  },
  {
    label: "Invoice Date",
    value: "sot_custom_invoice_date",
  },
  {
    label: "Behalf Of",
    value: "customer_relationship",
  },
  {
    label: "Amount",
    value: "sot_total_amount",
  },
  {
    label: "",
    value: "actions",
  },
];
// Items of a selected past bill — sourced from get-sales-items-by-sot-id.
const pastProductsHeading = [
  {
    label: "",
    value: "action",
    width: "4%",
  },
  {
    label: "Product Name",
    value: "item_name",
    width: "32%",
  },
  {
    label: "Sold Qty",
    value: "item_quantity",
    width: "8%",
  },
  {
    label: "Rate",
    value: "item_unit_rate",
    width: "8%",
  },
  {
    label: "Disc %",
    value: "item_discount_percentage",
    width: "7%",
  },
  {
    label: "MRP",
    value: "item_unit_price",
    width: "8%",
  },
  {
    label: "Avl Qty",
    value: "total_sellable_quantity",
    width: "10%",
  },
  {
    label: "Qty ",
    value: "quantity",
    width: "14%",
  },
];

// Top Products tab — the top-bills endpoint names a few fields differently
// from the past-bills one ("Sold Qty" comes back as sold_qty, "Avl Qty" as
// item_sellable_quantity), so remap those columns onto its payload.
const topProductsFieldMap = {
  item_quantity: "sold_qty",
  total_sellable_quantity: "item_sellable_quantity",
};
const topProductsHeading = pastProductsHeading.map((heading) =>
  topProductsFieldMap[heading.value]
    ? { ...heading, value: topProductsFieldMap[heading.value] }
    : heading
);

